// S3 작전 구성 — **껍데기와 탭 자리**
//
// 이 스텝이 만드는 것은 셸까지다. 세 탭의 내용은 각각 명부, 작전 구성, 수비 스텝이
// 채운다. 그래도 **탭 바를 여기서 마운트하는 것이 이 스텝의 일**이다 — 마운트하지
// 않으면 뒤 세 스텝이 자기 슬롯을 채워도 그것이 어느 화면에도 나타나지 않는다.
// `tabbar.tsx` 는 발사대 스텝이 소유하므로 **내용을 고치지 않고 불러다 그린다.**
//
// 이 화면에 room 을 만드는 통로는 없다. room 목록도, 추가·전환도 없다 — 관리 코드
// 하나가 room 하나를 연다.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { PresenceEntry, RoomCodes, WhitelistEntry } from '../../../../contract/schema.ts'
import {
  compose as composeStrings,
  shell as shellStrings,
  view as viewStrings,
  type Lang,
} from '../../../../i18n/index.ts'
import {
  call,
  clearToken,
  CT_TOKEN_KEY,
  forgetCtToken,
  markCtGone,
  otherTabCtToken,
  readCtGoneNotice,
  type CtGoneCause,
  readToken,
  routes,
  useLiveState,
} from '../../../access/client.ts'
import { attackBand, attackBandText, defenseBandText } from '../../../shell/band.ts'
import { TopBar, type TopBarLower } from '../../../shell/topbar.tsx'
import { enemyLines } from '../../../defense/derive.ts'
import { useDefense } from '../../../defense/use-defense.ts'
import { useViewSequence } from '../../view-attack/use-view-sequence.ts'
import { Icon, IconTile } from '../../../ui/icons.tsx'
import { useShellClock } from '../../../shell/use-shell-clock.ts'
import { ComposeCueContext } from './cue.ts'
import { ComposeTabBar, ComposeTabPanel, type ComposeTabId } from './tabbar.tsx'
import { useLang } from '../../../shell/use-lang.ts'

export function ComposeScreen(): React.ReactElement {
  const clock = useShellClock()
  // **고른 언어는 이 화면의 상태가 아니다.** 화면 뿌리마다 따로 들면 EN 을 눌러도 그
  // 화면에서만 영어이고 새로고침이나 주소 이동에 한국어로 돌아간다. 훅 하나가 저장소에
  // 두고 뿌리 셋이 같은 값을 본다.
  const { lang, choose: setLang } = useLang()
  const t = composeStrings(lang)

  const [ctToken, setCtToken] = useState<string | null>(() => readToken(CT_TOKEN_KEY))
  // 서버가 이 CT 세션을 거뒀는가. 거뒀으면 왜인가. null 이면 거두지 않았다. 토큰이 없이 열렸으면 이 탭이 앞서
  // 남긴 표지를 읽는다. 새로고침해도 이유가 남게 하려는 것이다.
  const [ctGone, setCtGone] = useState<CtGoneCause | null>(() =>
    readToken(CT_TOKEN_KEY) === null ? readCtGoneNotice() : null,
  )
  const [tab, setTab] = useState<ComposeTabId>('roster')
  // 「다음:」 줄이 가리키는 탭. 탭 화면이 `useComposeCue` 로 채운다.
  const [cue, setCue] = useState<ComposeTabId | null>(null)
  const [presenceOpen, setPresenceOpen] = useState(false)
  const [ctNickname, setCtNickname] = useState<string | null>(null)
  const [nicknameDraft, setNicknameDraft] = useState('')
  const [error, setError] = useState('')
  const [checked, setChecked] = useState(false)

  const live = useLiveState(ctToken)

  /**
   * **상단 고정줄 아래층의 재료** (D4F-0-01 수정, D4 미리보기 c2).
   *
   * 예전에는 이 자리가 `remaining: null, summary: ''` 상수였다. 그래서 confirm 뒤에도 CT 의 고정줄이
   * 「T− --:-- ops 없음」을 말했고, 같은 순간 연맹원 화면은 T− 를 셌다. 이제 열람과 같은 훅과 같은 계산
   * (`src/web/shell/band.ts`)을 쓴다.
   *
   * 탭 본문의 저장소(`useSequence`, 탭 안의 `useDefense`)는 탭 컴포넌트 안에 살아 껍데기가 빌릴 길이 없다.
   * 그래서 껍데기가 같은 훅을 한 벌 더 부른다. **새 라우트도 새 소켓도 없다**: 소켓은 `subscribeServerEvents` 가
   * 토큰당 하나를 나눠 준다. 읽기가 느는 경우는 넷이다(세션이 두 훅의 코드로 셈). 접속과 재접속마다 다섯 번(시퀀스, 명부 둘, 적 rally,
   * room), `presence.updated` 방송마다 명부 두 번(두 훅이 각자 읽는다), `defense.updated` 방송마다 명부 한 번, 유휴 초기화
   * (`room.reset`)마다 세 번(적 rally, 명부, room)이다. 탭 본문의 저장소가
   * 하는 읽기는 이와 별개로 그대로다.
   * 구성 본문의 delta 전용 계약은 그대로다. 절대 시각을 읽는 것은 껍데기의 고정줄뿐이고, 정본이 그 규칙의
   * 단위를 본문이라고 적었다.
   */
  const bandSequence = useViewSequence(ctToken, viewStrings(lang).rosterGone)
  const bandDefense = useDefense(ctToken)

  /**
   * **서버가 세션을 거두면 관리 코드 입력 안내로 돌아간다** (배포 전 보수 회차, 2026-10-07). 관리
   * 코드를 재발급하면 누른 CT 만 남고 다른 CT 세션은 끊긴다(사용자 결정). 전에는 그 닫힘을 받고도
   * 화면이 그대로 서 있어서, 끊긴 쪽은 누르는 것마다 거절을 읽으면서 왜인지 몰랐다.
   *
   * 죽은 토큰은 지운다. 남기면 새로고침마다 같은 토큰으로 다시 붙어 같은 닫힘을 받는다. 다만 저장 칸에
   * 이 탭의 죽은 토큰이 있을 때만이다. 다른 탭이 새로 들어와 써 둔 토큰은 남긴다(`forgetCtToken`).
   */
  //
  // **쥔 토큰을 의존성이 아니라 ref 로 읽는다.** 입장 화면과 같은 함정이다. 의존성에 넣으면 토큰이 바뀌는
  // 렌더에서 이 효과가 다시 도는데, 그때 `live.sessionGone` 은 아직 앞 토큰의 값이다.
  const ctTokenRef = useRef(ctToken)
  ctTokenRef.current = ctToken
  useEffect(() => {
    const dead = ctTokenRef.current
    if (!live.sessionGone || dead === null) return
    const cause: CtGoneCause = otherTabCtToken(dead) !== null ? 'other-tab' : (live.goneCause ?? 'unknown')
    forgetCtToken(dead)
    markCtGone(cause)
    setCtGone(cause)
    setCtToken(null)
  }, [live.sessionGone, live.goneCause])

  // 처음 한 번 room 을 읽어 닉네임이 비었는지 본다. WebSocket 이 붙기 전에도 1회성
  // 안내를 띄울지 정해야 하므로, 소켓만 기다리지 않는다.
  useEffect(() => {
    if (ctToken === null) {
      setChecked(true)
      return
    }
    void (async () => {
      const result = await call<{ operation: string; ctNickname: string | null }>(routes.roomPublic, {
        token: ctToken,
      })
      if (result.ok) setCtNickname(result.data.ctNickname)
      setChecked(true)
    })()
  }, [ctToken])

  useEffect(() => {
    if (live.room !== null) setCtNickname(live.room.ctNickname)
  }, [live.room])

  /**
   * **CT 가 지금 연 탭이 연맹원의 기본 탭을 정한다** (2026-08-15 결정, 조작을 하나 더 만들지
   * 않는다). defense 탭을 열면 defense, 그 밖의 탭(명부, attack)을 열면 attack 이다.
   *
   * **열 때 적고 떠날 때 되돌리지 않는다** (T2 개정 둘째, 2026-10-01). 예전에는 수비 탭이 떠날
   * 때의 정리 함수로 attack 을 되돌렸다. CT 가 DEFENSE 탭에 머문 채 브라우저를 닫으면 그 함수가
   * 돌지 않아 값이 defense 로 굳었고, 연맹원이 「ops 열람으로」를 눌러 「수비에는 signal 이
   * 없습니다」에 착지했다. CT 가 다시 들어와 ATTACK 을 눌러도 풀리지 않았다(2026-09-17 대리
   * 관측). 구성 화면은 명부 탭으로 열리므로, 이제는 CT 가 다시 들어오는 것만으로 풀린다.
   *
   * 실패는 화면에 올리지 않는다. 이 값은 CT 의 조작이 아니라 CT 가 보는 것의 그림자라, 거절
   * 문구를 띄우면 CT 는 누르지도 않은 것의 실패를 읽는다. 다음 탭 전환이 다시 적는다.
   */
  useEffect(() => {
    if (ctToken === null) return
    void call(routes.roomDefaultView, {
      body: { operation: tab === 'defense' ? 'defense' : 'attack' },
      token: ctToken,
    })
  }, [tab, ctToken])

  const closePresence = useCallback((): void => {
    setPresenceOpen(false)
  }, [])
  const presenceRef = useModalBehaviour(presenceOpen, closePresence)

  const saveNickname = useCallback(async (): Promise<void> => {
    setError('')
    const result = await call<{ room: { ctNickname: string | null } }>(routes.ctNickname, {
      body: { ctNickname: nicknameDraft },
      token: ctToken,
    })
    if (!result.ok) {
      setError(result.message)
      return
    }
    setCtNickname(result.data.room.ctNickname)
  }, [ctToken, nicknameDraft])

  /**
   * 배지와 두 값은 **CT 가 지금 연 탭**을 따른다. 명부와 ATTACK 은 attack, DEFENSE 는 defense 다(정본의 「지금 보고
   * 있는 탭」, 연맹원 기본 탭 규칙과 같은 묶음). room 의 `operation` 을 읽지 않는 이유는 기본 탭 쓰기가 방송되지
   * 않아 그 값이 소켓이 붙을 때의 값에서 멈추기 때문이다.
   */
  const shellT = shellStrings(lang)
  const bandLower = useMemo<TopBarLower>(() => {
    if (tab === 'defense') {
      return {
        opType: 'defense',
        ...defenseBandText(
          enemyLines(bandDefense.enemyRallies, bandDefense.rosters, clock.utcMillis),
          clock.utcMillis,
          shellT,
        ),
      }
    }
    return {
      opType: 'attack',
      ...attackBandText(
        attackBand(
          {
            state: bandSequence.sequence?.state ?? null,
            rallyCount: bandSequence.sequence?.rallies.length ?? 0,
            rows: bandSequence.rows,
          },
          clock.utcMillis,
        ),
        shellT,
        'compose',
      ),
    }
  }, [tab, bandDefense.enemyRallies, bandDefense.rosters, bandSequence.sequence, bandSequence.rows, clock.utcMillis, shellT])

  if (ctToken === null) {
    // 조용히 진입 화면으로 튕기지 않는다. 왜 못 들어왔는지를 말해야 CT 가 "코드를
    // 넣어야 하는구나" 를 알고, 아니면 앱이 고장 난 것으로 읽는다.
    // 상단 줄과 KO/EN 을 함께 그린다. 없으면 언어를 바꿀 길이 없고 다른 화면과 모양이 갈린다(대리 관측 둘째 판).
    return (
      <>
      <TopBar
        utcMillis={clock.utcMillis}
        skew={clock.skew}
        // **room 에 붙지 못했으니 타입도 남은 시간도 요약도 모른다.** 아래층 높이는 그대로 두고 상태 한 줄만 둔다
        // (2026-10-13 사용자 지시). 다른 탭이 이어 쓰는 경우는 코드가 필요한 것이 아니라 그 탭에서 계속하거나 이 탭을
        // 새로고침하면 되므로 다른 낱말을 쓴다. 다시 넣는 동작은 아래 본문의 주 버튼 하나다.
        lower={
          ctGone === 'other-tab'
            ? { kind: 'notice', reason: 'other-tab', text: shellT.bandOtherTab }
            : { kind: 'notice', reason: 'need-code', text: shellT.bandNeedCode }
        }
        lang={lang}
        onLangChange={setLang}
        // 표지는 1024 이상 위층 왼쪽에 선다(2026-10-13 사용자). 이 상태는 본문이 가운데 카드 한 장이라 표지와 아래층 상태 칩을 그 카드의
        // 왼쪽 끝에 맞춘다(2026-10-13 세션 선택, 비평 둘의 조건). 고정줄 폭은 모든 화면에서 하나라(2026-10-15 사용자 결정) 열은 그대로 두고
        // 두 자리만 옮긴다(`bodyCard`, 2026-10-15 세션 선택). KO/EN 은 다른 화면과 같은 자리다.
        brand
        bodyCard
      />
      <main className="col-narrow-card col-mid-card card mt-section font-sans [--card-py:var(--spacing-block)]" data-testid="compose-need-ct">
        {/* 장식(aria-hidden): 끊김 카드의 표지 타일. 글자는 아래 문단이 그대로 진다. */}
        {/* c2: 고정줄 아래층 칩과 같은 아이콘(관리 코드 필요는 열쇠, 다른 탭은 겹친 층). */}
        <IconTile name={ctGone === 'other-tab' ? 'layers' : 'key'} tone="neutral" size="lg" className="mb-group" />
        {ctGone === null ? null : (
          <p className="mb-line text-base font-semibold" role="alert" data-testid="compose-session-ended">
            {ctGone === 'rotated'
              ? t.ctSessionRotated
              : ctGone === 'other-tab'
                ? t.ctSessionOtherTab
                : t.ctSessionEnded}
          </p>
        )}
        {/* 다른 탭이 이어 쓰는 경우에는 규칙 문장을 빼고 그 안내만 남긴다. 둘이 함께 서면 결이 어긋났다(대리 관측 넷째 판). */}
        {ctGone === 'other-tab' ? null : <p className="text-sm text-ink-dim">{t.needCt}</p>}
        {/* 표시 방식은 inline-flex 다. 열람 화면의 「입장 화면으로」에 적은 클래스 이름 충돌을 피한다.
            **관리 코드 칸을 바로 연다**(`/?ct`). 문장은 관리 코드를 넣으라고 하는데 연맹원 입장 칸으로 보내면
            CT 링크를 한 번 더 찾아야 했다(2026-10-07 대리 관측). */}
        {/* 다른 탭이 이어 쓰는 경우에는 길을 그리지 않는다. 문장은 그 탭으로 가라고 하는데 관리 코드를 넣는 버튼이
            서면 다음 할 일이 둘로 갈린다(2026-10-07 대리 관측 셋째 판). */}
        {/* 다른 탭이 이어 쓰는 경우의 길은 이 탭 새로고침 하나다. 문장만 있고 누를 것이 없으면 모바일에서
            새로고침을 브라우저 메뉴에서 찾아야 했다(2026-10-12 대리 관측, 사용자 결정으로 버튼을 둔다). */}
        {ctGone === 'other-tab' ? (
          <button
            type="button"
            data-testid="compose-reload-tab"
            className="btn btn-primary mt-block"
            onClick={() => {
              window.location.reload()
            }}
          >
            {t.ctSessionReloadTab}
          </button>
        ) : (
          <a
            className="btn btn-primary mt-block"
            href="/?ct"
          >
            {t.needCtLink}
          </a>
        )}
      </main>
      </>
    )
  }

  return (
    <>
      <TopBar
        utcMillis={clock.utcMillis}
        skew={clock.skew}
        lower={bandLower}
        lang={lang}
        onLangChange={setLang}
        largeClock
        brand
        joined
      />

      <main className="col-wide pb-block font-sans" data-testid="compose-screen">
        <ComposeCueContext.Provider value={setCue}>
        {/* c2 장식 감싸개: 화면 머리와 탭 줄을 화면 전체 폭의 어두운 머리 띠 하나로 묶는다(빛 번짐 대신).
            **요소 순서는 1024 이상에서 보이는 순서(제목, 탭, 접속 명단)다** (2026-10-13 사용자, 키보드 초점 순서를 보이는 순서와 맞춘다).
            1024 미만은 두 줄(제목과 접속 명단, 그 아래 탭)이라 접속 명단이 탭보다 위에 서지만 초점은 탭 다음에 온다. 자리는 index.css 의
            `page-band` 규칙이 잡는다. */}
        <div className="page-band pt-block">
          <h1 className="page-title text-lg font-semibold">
            {/* 1024 이상에서는 고정줄 위층의 제품 표지가 같은 역할을 하므로 이 타일을 걷는다(둘이 겹치면 표지가 두 번 선다). */}
            <IconTile name="mark" tone="accent" size="lg" className="lg:hidden" />
            {t.title}
          </h1>
          {/* 발사대 스텝이 세워 둔 탭 바를 그대로 그린다. 슬롯 셋은 전부 찼다(2026-08-27 에
              rally 탭이 attack 탭에 흡수돼 넷에서 셋이 됐다). */}
          <div className="page-tabs">
            <ComposeTabBar active={tab} onSelect={setTab} cue={cue} />
          </div>
          {/* **여는 문 하나뿐이다.** 토글이던 시절에는 같은 버튼이 열고 닫았는데, 모달은
              자기 안에 닫는 문 셋(x, 바깥, Escape)을 들고 있어서 이 버튼은 여는 일만 한다.
              라벨이 상태에 따라 바뀌지 않는 것이 그 뜻이다. */}
          <button
            type="button"
            data-testid="presence-open"
            aria-haspopup="dialog"
            aria-expanded={presenceOpen}
            className="page-presence btn btn-secondary"
            onClick={() => {
              setPresenceOpen(true)
            }}
          >
            <Icon name="users" />
            {t.drawerOpen}
          </button>
        </div>
        <div className="mt-block">
            <ComposeTabPanel active={tab} />
        </div>
        </ComposeCueContext.Provider>

      </main>

      {/* ── 접속 명단 모달 ────────────────────────────────────────────────────
          **아래에 붙는 서랍이 아니라 모달이다** (2026-08-28 사용자 지시). 서랍이던 시절에는
          명단을 펴 놓은 채로 뒤의 탭을 계속 조작할 수 있었고, 그래서 이 자리가 「목록을
          손보는 동안」인지 「작전을 짜는 동안」인지가 화면에 없었다. 모달은 그 둘을 시간으로
          가른다. 열려 있는 동안은 목록 조작만 한다.

          덮개가 `fixed inset-0` 인 것으로 마우스는 막힌다. 그러나 **Tab 은 덮개를 통과한다.**
          그래서 초점 가두기가 따로 필요하고, 그것이 `useModalBehaviour` 가 하는 일이다. */}
      {presenceOpen && (
        <div
          data-testid="presence-modal"
          className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-block"
          role="dialog"
          aria-modal="true"
          aria-label={t.drawerOpen}
          onMouseDown={(event) => {
            // **누른 자리로 판정한다.** `onClick` 으로 보면 모달 안에서 눌러 시작해 바깥에서
            // 뗀 드래그가 「바깥 누름」으로 읽혀, 글자를 긁어 복사하다 창이 닫힌다.
            if (event.target === event.currentTarget) closePresence()
          }}
        >
          <div
            ref={presenceRef}
            tabIndex={-1}
            className="modal w-full max-w-md"
          >
            <div className="panel-head cat-neutral justify-between gap-2">
              <h2 className="flex items-center gap-2.5 text-base font-semibold">
                <IconTile name="users" tone="neutral" size="sm" />
                {t.drawerOpen}
              </h2>
              <button
                type="button"
                data-testid="presence-close"
                aria-label={t.drawerClose}
                className="btn btn-ghost min-w-11"
                onClick={closePresence}
              >
                x
              </button>
            </div>
            <div className="mt-group">
              <PresenceDrawer entries={live.entries} lang={lang} ctToken={ctToken} />
              <CodePanel lang={lang} ctToken={ctToken} />
              <WhitelistPanel lang={lang} ctToken={ctToken} presence={live.entries} />
            </div>
          </div>
        </div>
      )}

      {/* 1회성 단계 안내. 닉네임이 채워지기 전에는 올바른 입장 코드도 거절되므로,
          이것이 이 room 을 여는 실제 첫 걸음이다. */}
      {checked && ctNickname === null && (
        <div
          data-testid="ct-onboarding"
          className="fixed inset-0 z-20 flex items-center justify-center bg-black/70 px-4 py-block"
          role="dialog"
          aria-modal="true"
          aria-label={t.onboardingTitle}
        >
          <div className="modal w-full max-w-sm">
            <h2 className="panel-head cat-accent text-lg font-semibold">
              <IconTile name="mark" tone="accent" />
              {t.onboardingTitle}
            </h2>
            <p className="mt-line text-sm text-ink-dim">{t.onboardingWhy}</p>
            <label className="section-title caps mt-block block" htmlFor="ct-nickname-input">
              {t.onboardingNickname}
            </label>
            <input
              id="ct-nickname-input"
              data-testid="ct-nickname-input"
              className="field mt-line text-base"
              value={nicknameDraft}
              autoComplete="off"
              onChange={(event) => {
                setNicknameDraft(event.target.value)
              }}
            />
            {error !== '' && (
              <p role="alert" className="mt-group text-sm text-red-300">
                {error}
              </p>
            )}
            <button
              type="button"
              data-testid="ct-nickname-save"
              disabled={nicknameDraft.trim() === ''}
              className="btn btn-primary mt-group w-full"
              onClick={() => void saveNickname()}
            >
              {t.onboardingSave}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * 모달 하나가 지켜야 하는 것 셋. Escape 로 닫기, 배경 스크롤 잠그기, 초점 가두기.
 *
 * **초점 가두기를 뺄 수 없는 이유**는 이 회차의 요구가 「뒷 화면과 상호작용 불가능」이기
 * 때문이다. 덮개 `div` 는 마우스만 막는다. Tab 은 그것을 통과해 뒤 화면의 버튼으로 넘어가고,
 * 그러면 키보드로 쓰는 사람에게는 모달이 열려 있다는 사실이 화면에만 있고 조작에는 없다.
 *
 * **닫을 때 초점을 열던 버튼으로 돌려준다.** 안 돌려주면 초점이 `body` 로 떨어져, 닫은
 * 직후의 Tab 이 화면 맨 처음부터 다시 시작한다.
 */
function useModalBehaviour(
  open: boolean,
  close: () => void,
): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const root = ref.current
    root?.focus()

    const body = document.body
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close()
        return
      }
      if (event.key !== 'Tab' || root === null) return
      const stops = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      )
      const first = stops[0]
      const last = stops[stops.length - 1]
      // 길이 검사가 아니라 값 검사를 하는 이유는 `noUncheckedIndexedAccess` 다. 길이를
      // 봐도 색인 결과는 여전히 `undefined` 를 품은 타입이라 좁혀지지 않는다.
      if (first === undefined || last === undefined) return
      const active = document.activeElement
      // 상자 자체(`root`)가 초점을 들고 있을 때 Shift+Tab 을 누르면 뒤로 나가려 하므로,
      // 그 경우도 맨 끝으로 감는다. 열자마자 뒤로 눌러 빠져나가는 길이 여기다.
      if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      body.style.overflow = previousOverflow
      opener?.focus()
    }
  }, [open, close])

  return ref
}

function PresenceDrawer(props: {
  entries: PresenceEntry[]
  lang: Lang
  ctToken: string
}): React.ReactElement {
  const t = composeStrings(props.lang)
  const [rows, setRows] = useState<PresenceEntry[]>(props.entries)

  useEffect(() => {
    setRows(props.entries)
  }, [props.entries])

  const kick = useCallback(
    async (gameId: string): Promise<void> => {
      const result = await call<{ entries: PresenceEntry[] }>(routes.ctKick, {
        body: { gameId },
        token: props.ctToken,
      })
      if (result.ok) setRows(result.data.entries)
    },
    [props.ctToken],
  )

  return (
    // 눈에 보이는 소제목을 두지 않는다. 모달의 제목이 바로 위에서 같은 말을 하고 있어서,
    // 두면 같은 낱말이 두 줄 연속으로 선다. 낭독기용 이름은 `aria-label` 로 남는다.
    <section aria-label={t.presenceTitle}>
      {rows.length === 0 ? (
        <p className="mt-group text-sm text-ink-mute">{t.presenceEmpty}</p>
      ) : (
        <ul className="mt-group divide-y divide-line-hair">
          {rows.map((row) => (
            <li
              key={row.gameId}
              data-testid="presence-row"
              data-game-id={row.gameId}
              data-connections={String(row.connections)}
              className="flex items-center justify-between gap-2 py-line text-sm"
            >
              <span className="truncate font-medium">{row.nickname}</span>
              <span className="font-mono tabular-nums text-ink-dim">
                {t.connections} {row.connections}
              </span>
              <button
                type="button"
                data-testid="kick"
                className="btn btn-ghost"
                onClick={() => void kick(row.gameId)}
              >
                {t.kick}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * 코드 패널 — 관리 코드를 되찾는 세 경로 중 첫째.
 *
 * **기본은 가려진 상태다.** CT 는 이 화면을 전투 내내 띄워 두고, 그 화면은 화면 공유나
 * 어깨 너머로 남에게 보인다. 상시 노출이면 코드가 새는 경로가 화면 그 자체가 된다.
 */
function CodePanel(props: { lang: Lang; ctToken: string }): React.ReactElement {
  const t = composeStrings(props.lang)
  const [codes, setCodes] = useState<RoomCodes | null>(null)
  const [shown, setShown] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (): Promise<void> => {
    const result = await call<RoomCodes>(routes.ctCodes, { token: props.ctToken })
    if (result.ok) {
      setCodes(result.data)
      setError('')
    } else {
      setError(result.message)
    }
  }, [props.ctToken])

  const rotate = useCallback(
    async (target: 'admin' | 'join'): Promise<void> => {
      const result = await call<RoomCodes>(routes.ctCodesRotate, {
        body: { target },
        token: props.ctToken,
      })
      if (result.ok) {
        setCodes(result.data)
        setError('')
      } else {
        setError(result.message)
      }
    },
    [props.ctToken],
  )

  return (
    <section className="card-rule mt-block pt-group" aria-label={t.codesTitle}>
      <div className="flex items-center justify-between">
        <h2 className="section-title caps flex items-center gap-2">
          <Icon name="key" className="text-ink-mute" />
          {t.codesTitle}
        </h2>
        <button
          type="button"
          data-testid="codes-toggle"
          className="btn btn-secondary"
          onClick={() => {
            const next = !shown
            setShown(next)
            if (next && codes === null) void load()
          }}
        >
          {shown ? t.codesHide : t.codesReveal}
        </button>
      </div>

      {error !== '' && (
        <p role="alert" className="mt-group text-sm text-red-300">
          {error}
        </p>
      )}

      {shown && codes !== null && (
        <dl className="table-head mt-group grid grid-cols-[auto_1fr] gap-x-3 gap-y-line font-mono text-sm">
          <dt className="text-ink-mute">{t.codesJoin}</dt>
          <dd data-testid="codes-join">{codes.joinCode}</dd>
          <dt className="text-ink-mute">{t.codesAdmin}</dt>
          <dd data-testid="codes-admin">{codes.adminCode}</dd>
          <dt className="text-ink-mute">{t.codesRoomCount}</dt>
          <dd data-testid="codes-room-count">{codes.roomCount}</dd>
          <dt className="text-ink-mute">{t.codesIssuedAt}</dt>
          <dd>{new Date(codes.issuedAt).toISOString()}</dd>
        </dl>
      )}

      {shown && (
        <>
          <p className="mt-group text-xs text-ink-mute">{t.codesWarning}</p>
          <div className="mt-group flex gap-2">
            <button
              type="button"
              data-testid="rotate-admin"
              className="btn btn-secondary flex-1"
              onClick={() => void rotate('admin')}
            >
              {t.codesRotateAdmin}
            </button>
            <button
              type="button"
              data-testid="rotate-join"
              className="btn btn-secondary flex-1"
              onClick={() => void rotate('join')}
            >
              {t.codesRotateJoin}
            </button>
          </div>
        </>
      )}
    </section>
  )
}

/**
 * whitelist — **명단이 이 제품의 방어선이다.** 그 사실을 화면이 한 줄로 말한다.
 *
 * **읽기 전용이다.** 사람을 올리는 것도 이름을 고치는 것도 명단에서 지우는 것도 roster
 * 뷰의 명부에서 한다. 셋 다 그 화면에 실제로 서 있으므로 안내 문장이 셋을 다 말한다 —
 * 2026-08-12 개정 회차가 그 줄들을 세우기 전까지는 「올리는 자리」만 말했다. **안내 문장은
 * 지금 실제로 있는 것만 말한다** — 없는 조작을 적으면 사람이 그것을 찾으러 가고, 못 찾은
 * 사람은 자기가 놓쳤다고 여긴다.
 * 여기에 등록 폼을 함께 두었더니 **아군을 올리는 화면이 둘**이 되었고, 「어느 쪽에
 * 넣어야 행군 시간이 같이 들어가는가」를 화면이 답하지 못했다. 라우트는 하나로 지켰으니
 * 데이터가 갈리지는 않았지만, 같은 일을 하는 문이 둘이면 CT 는 둘의 차이를 스스로
 * 추측하게 된다.
 *
 * **게임 ID 도 그리지 않는다.** 정본의 「목록 표시는 닉네임과 marching 만」이 명부에만
 * 걸리고 이 패널은 예외라고 읽어 왔는데, 등록 폼이 빠지고 나면 여기 남는 것이 등재된
 * 사람의 이름 목록뿐이라 그 규칙의 대상이 된다. 예외를 남기면 통과·불통 계약이 「어느
 * DOM 을 재느냐」에 따라 달라져 채점자의 재량이 된다.
 *
 * **접속 명단과 합치지 않는다.** 앞은 「지금 누가 들어와 있는가」이고 이것은 「누가 들어올
 * 수 있는가」다. 합치면 접속 0 인 줄이 명단에 섞여, 신호를 받을 사람이 실제로 들어왔는지
 * 세려는 CT 가 눈으로 걸러야 한다 — 그것이 접속 명단의 유일한 용도다.
 *
 * **서랍이 열릴 때 한 번 읽고 마는 구조였다가 2026-08-12에 바뀌었다.** 그때까지 이 패널이
 * 늦게 반영할 수 있는 것은 **사람을 더하는 일**뿐이었고, 방금 올린 사람이 여기 없으면
 * 서랍을 닫았다 열면 되는 정도라 값을 매기지 않았다. 명부에 **지우는 문**이 서면서 그
 * 근거가 무너졌다 — 같은 화면이 「지웠습니다」와 「이 사람은 명단에 있다」를 동시에 말하게
 * 되고, whitelist에 이름이 있다는 것은 곧 **그 사람은 코드만 알면 들어온다**는 뜻이다.
 * 연맹을 떠난 사람을 내린 CT가 그 이름을 계속 보면 지워졌는지를 화면에 물어야 한다.
 *
 * 그래서 접속 명단이 다시 방송될 때마다 함께 다시 읽는다. 명부를 고치는 문 셋(등록·이름
 * 수정·삭제)이 전부 그 방송을 부르므로 **새 신호를 만들지 않고 이미 있는 것을 탄다.**
 * 방송 객체는 메시지마다 새로 오므로 내용이 같아도 다시 읽는데, 그 편이 낫다 — 내용을
 * 견주어 거르면 「접속은 그대로인데 명부만 바뀐」 경우가 정확히 걸러진다.
 */
function WhitelistPanel(props: {
  lang: Lang
  ctToken: string
  presence: PresenceEntry[]
}): React.ReactElement {
  const t = composeStrings(props.lang)
  const [entries, setEntries] = useState<WhitelistEntry[]>([])
  const presence = props.presence

  useEffect(() => {
    void (async () => {
      const result = await call<{ entries: WhitelistEntry[] }>(routes.ctWhitelist, {
        token: props.ctToken,
      })
      if (result.ok) setEntries(result.data.entries)
    })()
    // `presence` 를 값으로 읽지 않고 **다시 읽을 때를 알리는 신호로만** 쓴다.
  }, [props.ctToken, presence])

  return (
    <section className="card-rule mt-block pt-group" aria-label={t.whitelistTitle}>
      <h2 className="section-title caps flex items-center gap-2">
        <Icon name="roster" className="text-ink-mute" />
        {t.whitelistTitle}
      </h2>
      <p className="mt-line text-xs text-ink-mute">{t.whitelistWhy}</p>
      <p className="mt-line text-xs text-ink-mute">{t.whitelistWhere}</p>

      {entries.length === 0 ? (
        <p className="mt-group text-sm text-ink-mute">{t.whitelistEmpty}</p>
      ) : (
        <ul className="mt-group divide-y divide-line-hair" data-testid="whitelist-rows">
          {entries.map((row) => (
            <li key={row.gameId} className="py-line text-sm">
              {row.nickname}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** 진입 화면이 남긴 CT 토큰을 지운다. 되돌림 경로가 필요할 때 쓴다. */
export function forgetCt(): void {
  clearToken(CT_TOKEN_KEY)
}
